import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Eye, EyeOff, Plus, Pencil, RotateCcw, CheckCircle2, BellRing } from "lucide-react";

const STALE_MS = 30 * 86400000;
import { http, apiError, imgUrl, formatPrice, timeAgo } from "../lib/api";
import { useAuth } from "../lib/auth";
import LocationPicker from "../components/site/LocationPicker";
import PlanStatus from "../components/site/PlanStatus";

function ProfileForm({ user, setUser }) {
  const [f, setF] = useState({ name: user.name, district: user.district, taluka: user.taluka });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setUser((await http.put("/me/profile", f)).data); toast.success("Profile saved"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="profile-form">
      <p className="font-display font-bold text-lg text-[var(--navy)]">माझी माहिती · Profile</p>
      <input className="field" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" />
      <p className="text-sm text-slate-600">Mobile: <b data-testid="profile-mobile">+91 {user.mobile}</b></p>
      <LocationPicker required district={f.district} taluka={f.taluka} onChange={(district, taluka) => setF({ ...f, district, taluka })} testPrefix="profile" />
      <button disabled={busy} className="h-11 px-5 rounded-lg bg-[var(--navy)] text-white font-semibold disabled:opacity-60" data-testid="profile-save-button">Save</button>
    </form>
  );
}

function MyAds() {
  const [ads, setAds] = useState(null);
  const load = () => http.get("/me/ads").then((r) => setAds(r.data)).catch(() => setAds([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("ही जाहिरात delete करायची?")) return;
    try { await http.delete(`/me/ads/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSold = async (ad) => {
    try {
      await http.post(`/me/ads/${ad.id}/sold`, { sold: !ad.sold });
      toast.success(ad.sold ? "जाहिरात पुन्हा AVAILABLE केली" : "SOLD म्हणून mark केले");
      load();
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const confirm = async (ad) => {
    try { await http.post(`/me/ads/${ad.id}/confirm`); toast.success("धन्यवाद! जाहिरात Available ठेवली"); load(); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const isStale = (ad) => !ad.sold && Date.now() - new Date(ad.confirmed_at || ad.created_at).getTime() > STALE_MS;
  const staleCount = ads ? ads.filter(isStale).length : 0;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="my-ads">
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-lg text-[var(--navy)]">माझ्या जाहिराती · My Ads</p>
        <Link to="/sell" className="text-sm font-semibold text-[var(--sell)] flex items-center gap-1" data-testid="my-ads-post-link"><Plus className="w-4 h-4" /> नवीन</Link>
      </div>
      {staleCount > 0 && (
        <div className="mt-3 rounded-xl bg-yellow-50 border border-yellow-300 p-3 text-sm flex gap-2" data-testid="my-ads-reminder-banner">
          <BellRing className="w-5 h-5 text-yellow-600 shrink-0" />
          <p><b>{staleCount} जाहिरात 30 दिवसांपेक्षा जुनी आहे.</b> वस्तू विकली गेली असेल तर SOLD करा, नाहीतर "अजून Available आहे" दाबा - खरेदीदारांना खरी माहिती मिळेल.</p>
        </div>
      )}
      <div className="mt-3 divide-y">
        {ads?.map((ad) => {
          const live = ad.visible_until > new Date().toISOString();
          return (
            <div key={ad.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`my-ad-${ad.id}`}>
              <div className="relative w-20 h-16 rounded bg-slate-100 overflow-hidden shrink-0">
                {ad.photos[0] && <img src={imgUrl(ad.photos[0])} alt="" className="w-full h-full object-cover" />}
                {ad.sold && <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-extrabold tracking-widest">SOLD</span>}
              </div>
              <div className="flex-1 min-w-[160px]">
                <Link to={`/ad/${ad.id}`} className="font-semibold truncate block hover:underline">{ad.title}</Link>
                <p className="text-xs text-slate-500">{formatPrice(ad.price)} · {timeAgo(ad.created_at)}</p>
                <span className={`text-xs font-semibold inline-flex items-center gap-1 ${ad.sold ? "text-[var(--sell)]" : live ? "text-green-700" : "text-slate-500"}`} data-testid={`my-ad-status-${ad.id}`}>
                  {ad.sold ? "SOLD · विकले गेले" : live ? <><Eye className="w-3.5 h-3.5" /> Live · Available</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}
                </span>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Link to={`/sell?edit=${ad.id}`} className="flex-1 sm:flex-none h-10 px-3 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-[var(--navy)] text-sm font-bold flex items-center justify-center gap-1 transition-colors" data-testid={`my-ad-edit-${ad.id}`}><Pencil className="w-4 h-4" /> EDIT</Link>
                <button onClick={() => toggleSold(ad)} className={`flex-1 sm:flex-none h-10 px-3 rounded-lg text-white text-sm font-bold flex items-center justify-center gap-1 transition-colors ${ad.sold ? "bg-[var(--buy)] hover:bg-[#0d47a1]" : "bg-green-600 hover:bg-green-700"}`} data-testid={`my-ad-sold-${ad.id}`}>
                  {ad.sold ? <><RotateCcw className="w-4 h-4" /> AVAILABLE करा</> : <><CheckCircle2 className="w-4 h-4" /> विकले गेले / SOLD</>}
                </button>
                <button onClick={() => del(ad.id)} className="flex-1 sm:flex-none h-10 px-3 rounded-lg bg-[var(--sell)] hover:bg-[#c62828] text-white text-sm font-bold flex items-center justify-center gap-1 transition-colors" data-testid={`my-ad-delete-${ad.id}`}><Trash2 className="w-4 h-4" /> DELETE</button>
              </div>
              {isStale(ad) && (
                <div className="w-full rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`my-ad-reminder-${ad.id}`}>
                  <BellRing className="w-4 h-4 text-yellow-600" />
                  <span className="flex-1">30 दिवस झाले - ही वस्तू विकली गेली का?</span>
                  <button onClick={() => confirm(ad)} className="h-8 px-3 rounded-lg bg-white border border-yellow-400 font-semibold text-[var(--navy)]" data-testid={`my-ad-confirm-${ad.id}`}>अजून Available आहे</button>
                </div>
              )}
            </div>
          );
        })}
        {ads && ads.length === 0 && <p className="py-6 text-center text-sm text-slate-500">अजून जाहिरात नाही.</p>}
      </div>
    </div>
  );
}

export default function Account() {
  const { user, setUser, signOut } = useAuth();
  if (user === null) return <div className="py-20 text-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login?next=/account" replace />;
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-5" data-testid="account-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-3xl text-[var(--navy)]">नमस्कार, {user.name}</h1>
        <button onClick={signOut} className="text-sm text-slate-600 hover:text-[var(--sell)] flex items-center gap-1" data-testid="member-logout-button"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
      <PlanStatus user={user} />
      <MyAds />
      <div className="grid md:grid-cols-2 gap-5">
        <ProfileForm user={user} setUser={setUser} />
      </div>
    </div>
  );
}
